import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Alert,
  AlertIcon,
  AlertBody,
} from '@lifeomic/chroma-react/components/Alert';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { AlertTriangle } from '@lifeomic/chromicons';
import { projectCohortPickerActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectCohortPicker';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { memberDetailMessages } from './messages';
import type { MemberHomeCohort } from './useMemberOutsideActiveCohort';

export type MemberCohortMismatchBannerProps = {
  show: boolean;
  homeCohort: MemberHomeCohort | null;
  isFindingHomeCohort: boolean;
};

const MemberCohortMismatchBanner: React.FC<MemberCohortMismatchBannerProps> = ({
  show,
  homeCohort,
  isFindingHomeCohort,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  if (!show) {
    return null;
  }

  const switchToCohort = (cohortId: string) => {
    dispatch(projectCohortPickerActions.setActiveProjectCohort(cohortId));
  };

  const switchToAllCohorts = () => {
    dispatch(projectCohortPickerActions.setActiveProjectCohort(null));
  };

  return (
    <Alert
      statusType="warning"
      fullWidth
      {...getTestProps('member-cohort-mismatch-banner')}
    >
      <AlertIcon icon={AlertTriangle} />
      <AlertBody>
        <Box direction="row" align="center" gap={1} flexWrap>
          <Text size="subbody">
            {formatMessage(memberDetailMessages.cohortMismatchIntro)}
          </Text>
          {isFindingHomeCohort ? <DotLoader size={0} /> : null}
          {!isFindingHomeCohort && homeCohort ? (
            <>
              <Button
                variant="text"
                onClick={() => switchToCohort(homeCohort.id)}
                {...getTestProps('member-cohort-mismatch-switch-cohort')}
              >
                {formatMessage(
                  memberDetailMessages.cohortMismatchSwitchCohort,
                  {
                    cohortName: homeCohort.name,
                  },
                )}
              </Button>
              <Text size="subbody">
                {formatMessage(memberDetailMessages.cohortMismatchOr)}
              </Text>
            </>
          ) : null}
          <Button
            variant="text"
            onClick={switchToAllCohorts}
            {...getTestProps('member-cohort-mismatch-all-cohorts')}
          >
            {formatMessage(memberDetailMessages.cohortMismatchAllCohorts)}
          </Button>
        </Box>
      </AlertBody>
    </Alert>
  );
};

MemberCohortMismatchBanner.displayName = 'MemberCohortMismatchBanner';
export default MemberCohortMismatchBanner;
